/**
 * AddCommunicationModal
 *
 * Modal for adding a communication log entry to a credit application.
 * Fields: communicationType, direction, communicationDate, subject,
 *         content, summary, fromName, toNames.
 *
 * Creates via POST /api/applications/:applicationId/communications
 * F006 AC005
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const COMMUNICATION_TYPE_OPTIONS = [
  { value: "submission", label: "Submission" },
  { value: "query", label: "Query" },
  { value: "response", label: "Response" },
  { value: "update", label: "Update" },
  { value: "approval", label: "Approval" },
  { value: "decline", label: "Decline" },
  { value: "condition_request", label: "Condition Request" },
];

const DIRECTION_OPTIONS = [
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
];

interface AddCommunicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
}

export function AddCommunicationModal({
  open,
  onOpenChange,
  applicationId,
}: AddCommunicationModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [communicationType, setCommunicationType] = useState("query");
  const [direction, setDirection] = useState("outbound");
  const [communicationDate, setCommunicationDate] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [summary, setSummary] = useState("");
  const [fromName, setFromName] = useState("");
  const [toNames, setToNames] = useState("");

  // Reset form and default date to today
  useEffect(() => {
    if (open) {
      setCommunicationType("query");
      setDirection("outbound");
      setCommunicationDate(new Date().toISOString().slice(0, 10));
      setSubject("");
      setContent("");
      setSummary("");
      setFromName("");
      setToNames("");
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", `/api/applications/${applicationId}/communications`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/communications`],
      });
      toast({ title: "Communication added" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add communication",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!subject.trim()) {
      toast({
        title: "Subject required",
        description: "Please enter a subject for the communication.",
        variant: "destructive",
      });
      return;
    }

    createMutation.mutate({
      communicationType,
      direction,
      communicationDate: communicationDate
        ? new Date(communicationDate).toISOString()
        : new Date().toISOString(),
      subject: subject.trim(),
      content: content || null,
      summary: summary || null,
      fromName: fromName || null,
      toNames: toNames || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]" data-testid="dialog-add-communication">
        <DialogHeader>
          <DialogTitle>Add Communication</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Communication Type */}
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={communicationType} onValueChange={setCommunicationType}>
              <SelectTrigger data-testid="select-communication-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {COMMUNICATION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Direction */}
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="select-direction">
                <SelectValue placeholder="Select direction" />
              </SelectTrigger>
              <SelectContent>
                {DIRECTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input
              type="date"
              value={communicationDate}
              onChange={(e) => setCommunicationDate(e.target.value)}
              data-testid="input-communication-date"
            />
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              placeholder="Subject of the communication"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-communication-subject"
            />
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <Label>Content</Label>
            <Textarea
              placeholder="Full content..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
              data-testid="input-communication-content"
            />
          </div>

          {/* Summary */}
          <div className="space-y-1.5">
            <Label>Summary</Label>
            <Textarea
              placeholder="Brief summary..."
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={2}
              data-testid="input-communication-summary"
            />
          </div>

          {/* From Name */}
          <div className="space-y-1.5">
            <Label>From</Label>
            <Input
              placeholder="Sender name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              data-testid="input-from-name"
            />
          </div>

          {/* To Names */}
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input
              placeholder="Recipient name(s)"
              value={toNames}
              onChange={(e) => setToNames(e.target.value)}
              data-testid="input-to-names"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
            data-testid="button-cancel-communication"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="button-submit-communication"
          >
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Communication
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
